import { type ReactNode } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import classNames from 'classnames';

import { trackAnalyticsEvent } from '@jsmdg/tracking';
import { Accordion, ReadMore, type Tab, Wysiwyg } from '@jsmdg/yoshi';
import { type AccordionItemId } from '@jsmdg/yoshi/dist/components/Accordion/AccordionContext';
import { isBox, isExperience } from '../../../shared/product-safeguards';
import {
    type BaseProductInformation,
    type ContentBlock,
    type ExperienceProductInformation,
    type Product,
} from '../../../shared/types';
import { AccordionTitle } from '../AccordionContainer/AccordionTitle';
import { getHowItWorksVariant, HowItWorks } from '../HowItWorks/HowItWorks';
import { Reviews } from '../Reviews';
import { Venues } from '../Venues';
import { useAccordionTracking } from './useAccordionTracking';
import accordionContainerStyles from '../AccordionContainer/AccordionContainer.module.scss';
import styles from '../AccordionContainer/AccordionContent.module.scss';

const baseStructuredProductInformationContentBlocks: Record<string, keyof BaseProductInformation> =
    {
        description: 'description',
        services: 'services',
    };

const experienceProductStructuredProductInformationContentBlocks: Record<
    string,
    keyof Omit<ExperienceProductInformation, 'caption'>
> = {
    duration: 'duration',
    availability: 'availability',
    conditions: 'conditions',
    weather: 'weather',
    equipmentAndClothing: 'equipmentAndClothing',
    participants: 'participants',
    restrictions: 'restrictions',
};

const allStructuredProductInformationContentBlocks = {
    ...baseStructuredProductInformationContentBlocks,
    ...experienceProductStructuredProductInformationContentBlocks,
};

function createContentBlockIdentifier(contentBlock: ContentBlock, index: number): string {
    return `${contentBlock?.caption || contentBlock.body.slice(0, 10)}-${index}`;
}

function renderContentBlock({
    contentBlock,
    product,
    showMap,
}: {
    contentBlock: ContentBlock;
    product: Product;
    showMap?: boolean;
}): JSX.Element | null {
    if (!contentBlock.body) {
        return null;
    }

    const displayableLocations = isExperience(product)
        ? product.locations.filter(location => location.zipCode !== '00000')
        : [];

    const canRenderLocationsMap =
        showMap &&
        isExperience(product) &&
        displayableLocations.length > 0 &&
        contentBlock?.name === 'availability' &&
        !product.voucherPartnerInformation?.iframeUrl;

    return (
        <div className="mb-1x" key={contentBlock.name}>
            {contentBlock.caption && <h2>{contentBlock.caption}</h2>}
            <Wysiwyg content={contentBlock.body} className="overflow-hidden" />
            {canRenderLocationsMap && <Venues product={product} />}
        </div>
    );
}

function renderCollapsibleGroups(
    contentBlocks: ContentBlock[],
    product: Product,
    onCollapsibleToggle: (index: AccordionItemId, open: boolean) => void,
    contentClass?: string,
): JSX.Element {
    return (
        <Accordion key="collapsible-groups" onSelect={onCollapsibleToggle}>
            {contentBlocks.map((contentBlock, index) => {
                return contentBlock.caption ? (
                    <Accordion.Item
                        key={contentBlock.caption}
                        id={contentBlock.caption}
                        title={
                            <AccordionTitle
                                showIcon={false}
                                caption={contentBlock.caption}
                                className={accordionContainerStyles.accordionItemTitle}
                            />
                        }
                        className="pb-2x"
                        contentClasses={classNames(contentClass, {
                            'pt-2x': !contentBlock.caption && index > 0,
                        })}
                    >
                        <Wysiwyg content={contentBlock.body} className="overflow-hidden" />
                    </Accordion.Item>
                ) : (
                    <div className="mt-1x" data-testid="non-accordion-item" key={contentBlock.name}>
                        {renderContentBlock({ contentBlock, product })}
                    </div>
                );
            })}
        </Accordion>
    );
}

function useBuildProductInfoAccordion({
    collapseText,
    expandText,
    moreDetailsCaption,
    product,
    showMap,
}: {
    product: Product;
    showMap: boolean;
    expandText: string;
    collapseText: string;
    moreDetailsCaption: string;
}): Tab[] {
    const structuredContentBlocks = [
        renderContentBlock({
            contentBlock: {
                ...product.productInformation.description,
                name: allStructuredProductInformationContentBlocks.description,
            },
            product,
            showMap,
        }),
    ];

    const unstructuredContentBlocks = product.productInformation.contentBlocks.map(
        (contentBlock, index) =>
            renderContentBlock({
                contentBlock: {
                    ...contentBlock,
                    name: createContentBlockIdentifier(contentBlock, index),
                },
                product,
                showMap,
            }),
    );

    const content = structuredContentBlocks.concat(unstructuredContentBlocks).filter(Boolean);
    const { toggleTracking } = useAccordionTracking();

    const wrapWithReadMore = (data: ReactNode): ReactNode => (
        <ReadMore
            autoScrollToContent={false}
            onClick={() => toggleTracking(expandText, collapseText)}
            expandText={expandText}
            collapseText={collapseText}
            rows={10}
        >
            {data}
        </ReadMore>
    );

    return isExperience(product)
        ? [
              {
                  caption: product.productInformation.caption,
                  content: wrapWithReadMore(content),
              },
              {
                  caption: moreDetailsCaption,
                  content: Object.values(experienceProductStructuredProductInformationContentBlocks)
                      .map(key =>
                          renderContentBlock({
                              contentBlock: {
                                  ...(product.productInformation[key] as ContentBlock),
                                  name: key,
                              },
                              product,
                              showMap,
                          }),
                      )
                      .filter(Boolean),
              },
          ]
        : [
              {
                  caption: product.productInformation.caption,
                  content: wrapWithReadMore(content),
              },
          ];
}

function buildContentGroupAccordions(product: Product, collapsibleContentClass?: string): Tab[] {
    return product.contentGroups.map(group => {
        const { caption, contentBlocks } = group;

        const renderableContentBlocks = contentBlocks.reduce<ContentBlock[]>(
            (acc, contentBlock, index) => {
                if (contentBlock.body) {
                    acc.push({
                        ...contentBlock,
                        name: createContentBlockIdentifier(contentBlock, index),
                    });
                }

                return acc;
            },
            [],
        );

        const onCollapsibleToggle = (_: AccordionItemId, open: boolean): void => {
            if (caption === 'FAQ' && open) {
                trackAnalyticsEvent({
                    category: 'ProductDetail',
                    action: 'Expand',
                    label: 'FAQs',
                });
            }
        };

        return {
            caption,
            content:
                renderableContentBlocks.length >= 5
                    ? [
                          renderCollapsibleGroups(
                              renderableContentBlocks,
                              product,
                              onCollapsibleToggle,
                              collapsibleContentClass,
                          ),
                      ]
                    : renderableContentBlocks.map(contentBlock =>
                          renderContentBlock({ contentBlock, product }),
                      ),
        };
    });
}

function buildVoucherPartnerContent(product: Product): Tab[] {
    const voucherPartnerInformation =
        isExperience(product) || isBox(product) ? product.voucherPartnerInformation : undefined;

    const url = voucherPartnerInformation?.iframeUrl;

    if (!url) {
        return [];
    }

    return [
        {
            caption: voucherPartnerInformation.iframeTabCaption || '',
            content: [
                // eslint-disable-next-line react/iframe-missing-sandbox
                <iframe
                    key={voucherPartnerInformation.iframeTabCaption}
                    title={voucherPartnerInformation.iframeTabCaption || ''}
                    src={url}
                    height="800px"
                    width="100%"
                    loading="lazy"
                />,
            ],
        },
    ];
}

interface BuildReviewTabProps {
    product: Product;
    caption: string;
    handleReviewPartnerScript?: () => void;
}

function buildReviewTab({
    caption,
    handleReviewPartnerScript,
    product,
}: BuildReviewTabProps): Tab[] {
    return !product.rating?.count
        ? []
        : [
              {
                  caption,
                  content: [<Reviews key="reviews" />],
                  buttonProps: {
                      onMouseOver: handleReviewPartnerScript,
                      onFocus: handleReviewPartnerScript,
                  },
              },
          ];
}

const messages = defineMessages({
    reviewsTabCaption: {
        defaultMessage: 'Kundenbewertungen',
    },
    moreDetailsCaption: {
        defaultMessage: 'Mehr Details',
    },
    seeMore: {
        defaultMessage: 'Mehr Lesen',
    },
    seeLess: {
        defaultMessage: 'Weniger Lesen',
    },
    howItWorksCaption: {
        defaultMessage: "So funktioniert's",
    },
});

function useBuildProductInfoAccordions(product: Product): Tab[] {
    const intl = useIntl();

    const showMap = false;
    const howItWorksVariant = getHowItWorksVariant(product);
    const howItWorksAccordion: Tab[] = howItWorksVariant
        ? [
              {
                  caption: intl.formatMessage(messages.howItWorksCaption),
                  content: <HowItWorks variant={howItWorksVariant} />,
              },
          ]
        : [];

    return useBuildProductInfoAccordion({
        product,
        showMap,
        expandText: intl.formatMessage(messages.seeMore),
        collapseText: intl.formatMessage(messages.seeLess),
        moreDetailsCaption: intl.formatMessage(messages.moreDetailsCaption),
    })
        .concat(buildContentGroupAccordions(product, styles.content))
        .concat(howItWorksAccordion)
        .concat(
            buildReviewTab({
                product,
                caption: intl.formatMessage(messages.reviewsTabCaption),
            }),
        );
}

function useProductInfoAccordions(product: Product): Tab[] {
    return useBuildProductInfoAccordions(product);
}

export { buildVoucherPartnerContent, useBuildProductInfoAccordions, useProductInfoAccordions };
